import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText, Image as ImageIcon, Loader2, Trash2, Upload, Video, Volume2, Lock } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty } from '@/components/ui/empty'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { isJobLocked, isJobModificationBlocked, isTerminalJobStatus } from '@/lib/job-status'
import { formatDate } from '@/lib/utils'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { jobsApi } from '@/services/jobs'
import type { Job, JobMediaItem } from '@/types'

export type MediaUploadKind = 'general' | 'before' | 'after'

export interface PendingJobMediaFile {
  id: string
  file: File
  kind: MediaUploadKind
  previewUrl: string
}

const DOCUMENT_ACCEPT = [
  'image/*',
  'video/*',
  'audio/*',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.csv',
].join(',')

const formatFileSize = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

const getMediaKindLabel = (kind: MediaUploadKind) => {
  if (kind === 'before') return 'Before'
  if (kind === 'after') return 'After'
  return 'Generic'
}

const getMediaKindLabelFromItem = (item: JobMediaItem) => {
  if (item.is_before) return 'Before'
  if (item.is_after) return 'After'
  return 'Generic'
}

const getMediaSource = (item: JobMediaItem) => {
  const fileUrl = String(item.file_url ?? '').trim()
  if (fileUrl) {
    return fileUrl
  }

  const file = String(item.file ?? '').trim()
  if (file.startsWith('http://') || file.startsWith('https://')) {
    return file
  }

  return null
}

const inferPendingFileType = (file: File): JobMediaItem['file_type'] => {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  return 'other'
}

export async function uploadPendingJobMedia(
  jobId: string,
  pendingFiles: PendingJobMediaFile[],
) {
  for (const item of pendingFiles) {
    await jobsApi.uploadMedia(jobId, item.file, {
      caption: item.file.name,
      is_before: item.kind === 'before',
      is_after: item.kind === 'after',
    })
  }
}

export function createPendingJobMediaFile(file: File, kind: MediaUploadKind): PendingJobMediaFile {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    kind,
    previewUrl: URL.createObjectURL(file),
  }
}

export function releasePendingJobMediaFiles(pendingFiles: PendingJobMediaFile[]) {
  for (const item of pendingFiles) {
    URL.revokeObjectURL(item.previewUrl)
  }
}

interface JobMediaGalleryEditorProps {
  job?: Job | null
  pendingFiles?: PendingJobMediaFile[]
  onPendingFilesChange?: (files: PendingJobMediaFile[]) => void
  title?: string
  description?: string
  allowUpload?: boolean
  /** Tenant IANA timezone for timestamps; omitted keeps the existing browser-timezone output. */
  timeZone?: string | null
}

const renderServerPreview = (item: JobMediaItem) => {
  const source = getMediaSource(item)

  if (!source) {
    return (
      <div className="flex h-36 items-center justify-center rounded-xl bg-muted/40">
        <FileText className="h-10 w-10 text-muted-foreground" />
      </div>
    )
  }

  if (item.file_type === 'image') {
    return <img src={source} alt={item.caption || item.file_name || 'Gallery media'} className="h-36 w-full rounded-xl object-cover" />
  }

  if (item.file_type === 'video') {
    return <video src={source} controls className="h-36 w-full rounded-xl bg-black object-cover" />
  }

  if (item.file_type === 'audio') {
    return (
      <div className="flex h-36 flex-col items-center justify-center rounded-xl bg-muted/40 p-4">
        <Volume2 className="mb-3 h-8 w-8 text-muted-foreground" />
        <audio src={source} controls className="w-full" />
      </div>
    )
  }

  return (
    <a
      href={source}
      target="_blank"
      rel="noreferrer"
      className="flex h-36 flex-col items-center justify-center rounded-xl bg-muted/40 p-4 text-center transition-colors hover:bg-muted"
    >
      <FileText className="mb-3 h-10 w-10 text-muted-foreground" />
      <span className="line-clamp-2 text-sm font-medium">{item.file_name || item.caption || 'Open file'}</span>
    </a>
  )
}

const renderPendingPreview = (item: PendingJobMediaFile) => {
  const fileType = inferPendingFileType(item.file)

  if (fileType === 'image') {
    return <img src={item.previewUrl} alt={item.file.name} className="h-36 w-full rounded-xl object-cover" />
  }

  if (fileType === 'video') {
    return <video src={item.previewUrl} controls className="h-36 w-full rounded-xl bg-black object-cover" />
  }

  if (fileType === 'audio') {
    return (
      <div className="flex h-36 flex-col items-center justify-center rounded-xl bg-muted/40 p-4">
        <Volume2 className="mb-3 h-8 w-8 text-muted-foreground" />
        <audio src={item.previewUrl} controls className="w-full" />
      </div>
    )
  }

  return (
    <div className="flex h-36 flex-col items-center justify-center rounded-xl bg-muted/40 p-4 text-center">
      <FileText className="mb-3 h-10 w-10 text-muted-foreground" />
      <span className="line-clamp-2 text-sm font-medium">{item.file.name}</span>
    </div>
  )
}

export function JobMediaGalleryEditor({
  job,
  pendingFiles = [],
  onPendingFilesChange,
  title,
  description,
  allowUpload = true,
  timeZone,
}: JobMediaGalleryEditorProps) {
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const isEventMediaMode = labels.mode === 'event' || String(job?.kind_code ?? '').toLowerCase() === 'event'
  const entityLower = labels.entityLower
  const entityPluralLower = labels.entityPluralLower
  const resolvedTitle = title ?? labels.galleryTitle
  const resolvedDescription = description ?? `Upload images, videos, and supporting files for this ${entityLower}.`
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingUploadKindRef = useRef<MediaUploadKind>('general')
  const [uploadKind, setUploadKind] = useState<MediaUploadKind>('general')
  const isPendingMode = !job?.id

  const configQuery = useQuery({
    queryKey: ['job-management-configuration'],
    queryFn: jobsApi.getManagementConfiguration,
    staleTime: 60_000,
  })

  const mediaQuery = useQuery({
    queryKey: ['job-media', job?.id, configQuery.data?.media_base_url ?? null],
    queryFn: () => jobsApi.getMedia(job!.id, configQuery.data?.media_base_url),
    enabled: Boolean(job?.id),
  })

  const deleteMutation = useMutation({
    mutationFn: (mediaId: string | number) => jobsApi.deleteMedia(mediaId),
    onSuccess: () => {
      toast.success('Media deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['job-media', job?.id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async ({ files, kind }: { files: File[]; kind: MediaUploadKind }) => {
      if (isPendingMode) {
        const next = [...pendingFiles]
        for (const file of files) {
          next.push(createPendingJobMediaFile(file, kind))
        }
        onPendingFilesChange?.(next)
        return next
      }

      const results: JobMediaItem[] = []
      for (const file of files) {
        const isBefore = !isEventMediaMode && kind === 'before'
        const isAfter = !isEventMediaMode && kind === 'after'
        const uploaded = await jobsApi.uploadMedia(job!.id, file, {
          caption: file.name,
          is_before: isBefore,
          is_after: isAfter,
        })
        results.push(uploaded)
      }
      return results
    },
    onSuccess: () => {
      if (isPendingMode) {
        toast.success('Files added to gallery')
        return
      }
      toast.success('Media uploaded successfully')
      queryClient.invalidateQueries({ queryKey: ['job-media', job?.id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const effectiveConfig = configQuery.data
  const maxFiles = effectiveConfig?.number_gallery_files ?? 10
  const maxFileSize = effectiveConfig?.maximum_file_size ?? 10 * 1024 * 1024
  const currentServerFiles = mediaQuery.data?.results.length ?? 0
  const currentFiles = currentServerFiles + pendingFiles.length
  const remainingFiles = Math.max(maxFiles - currentFiles, 0)

  const mediaSections = useMemo(() => {
    const results = mediaQuery.data?.results ?? []
    if (isEventMediaMode) {
      return [
        {
          key: 'generic',
          uploadKind: 'general' as MediaUploadKind,
          title: 'Generic',
          description: 'General documents, notes, and supporting files.',
          items: results.filter((item) => !item.is_before && !item.is_after),
          pendingItems: pendingFiles.filter((item) => item.kind === 'general'),
        },
      ]
    }

    return [
      {
        key: 'generic',
        uploadKind: 'general' as MediaUploadKind,
        title: 'Generic',
        description: 'General documents, notes, and supporting files.',
        items: results.filter((item) => !item.is_before && !item.is_after),
        pendingItems: pendingFiles.filter((item) => item.kind === 'general'),
      },
      {
        key: 'before',
        uploadKind: 'before' as MediaUploadKind,
        title: 'Before',
        description: 'Before-work evidence and condition photos.',
        items: results.filter((item) => item.is_before),
        pendingItems: pendingFiles.filter((item) => item.kind === 'before'),
      },
      {
        key: 'after',
        uploadKind: 'after' as MediaUploadKind,
        title: 'After',
        description: 'After-work evidence and completion proof.',
        items: results.filter((item) => item.is_after),
        pendingItems: pendingFiles.filter((item) => item.kind === 'after'),
      },
    ]
  }, [isEventMediaMode, mediaQuery.data?.results, pendingFiles])

  const isJobMediaMutable = useMemo(() => {
    if (isPendingMode) return true
    if (!job) return false
    return !isJobModificationBlocked(job)
  }, [isPendingMode, job])

  const helperText = useMemo(() => {
    if (isPendingMode) {
      if (remainingFiles === 0) {
        return `You have reached the configured media limit for this ${entityLower}.`
      }
      return `Add up to ${remainingFiles} image or video file${remainingFiles === 1 ? '' : 's'}. They will upload after the ${entityLower} is saved.`
    }

    if (job && isJobLocked(job)) {
      return `This ${entityLower} is locked. Media uploads and deletions are disabled.`
    }

    if (job && isTerminalJobStatus(job.status)) {
      return `Completed or cancelled ${entityPluralLower} keep their media history read-only.`
    }

    if (remainingFiles === 0) {
      return `This ${entityLower} has reached its configured media limit.`
    }

    return `You can upload up to ${remainingFiles} more file${remainingFiles === 1 ? '' : 's'} for this ${entityLower}.`
  }, [entityLower, entityPluralLower, isPendingMode, job, remainingFiles])

  const openFilePicker = (kind: MediaUploadKind = uploadKind) => {
    if (!allowUpload || !isJobMediaMutable || uploadMutation.isPending || remainingFiles === 0) {
      return
    }

    pendingUploadKindRef.current = kind
    setUploadKind(kind)
    fileInputRef.current?.click()
  }

  const removePendingFile = (id: string) => {
    const target = pendingFiles.find((item) => item.id === id)
    if (target) {
      URL.revokeObjectURL(target.previewUrl)
    }
    onPendingFilesChange?.(pendingFiles.filter((item) => item.id !== id))
  }

  const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''

    if (files.length === 0) {
      return
    }

    if (files.length > remainingFiles) {
      toast.error(`You can only upload ${remainingFiles} more file${remainingFiles === 1 ? '' : 's'} for this ${entityLower}.`)
      return
    }

    const oversizedFile = files.find((file) => file.size > maxFileSize)
    if (oversizedFile) {
      toast.error(`${oversizedFile.name} exceeds the ${formatFileSize(maxFileSize)} file size limit.`)
      return
    }

    await uploadMutation.mutateAsync({ files, kind: pendingUploadKindRef.current })
  }

  const renderMediaCard = (item: JobMediaItem) => {
    const source = getMediaSource(item)

    return (
      <div key={`server-${item.id}`} className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="p-3">{renderServerPreview(item)}</div>
        <div className="space-y-3 px-4 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-medium">{item.caption || item.file_name || 'Untitled media'}</p>
              <p className="text-xs text-muted-foreground">
                {item.uploaded_by_name || 'Uploaded by staff'}
                {item.created_at ? ` • ${formatDate(item.created_at, timeZone ? { timeZone } : undefined)}` : ''}
              </p>
            </div>
            <Badge variant="secondary">{getMediaKindLabelFromItem(item)}</Badge>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {item.file_type === 'image' ? <ImageIcon className="h-4 w-4" /> : null}
              {item.file_type === 'video' ? <Video className="h-4 w-4" /> : null}
              {item.file_type === 'audio' ? <Volume2 className="h-4 w-4" /> : null}
              {item.file_type === 'other' ? <FileText className="h-4 w-4" /> : null}
              <span className="capitalize">{item.file_type || 'file'}</span>
            </div>
            <div className="flex items-center gap-2">
              {source ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={source} target="_blank" rel="noreferrer">
                    Open
                  </a>
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => deleteMutation.mutate(item.id)}
                disabled={!isJobMediaMutable || deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPendingCard = (item: PendingJobMediaFile) => (
    <div key={`pending-${item.id}`} className="overflow-hidden rounded-2xl border border-dashed border-border/70 bg-card shadow-sm">
      <div className="p-3">{renderPendingPreview(item)}</div>
      <div className="space-y-3 px-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium">{item.file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(item.file.size)} • Pending upload</p>
          </div>
          <Badge variant="outline">{getMediaKindLabel(item.kind)}</Badge>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={() => removePendingFile(item.id)}
            disabled={uploadMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )

  const hasAnyMedia = currentFiles > 0

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="text-base">{resolvedTitle}</CardTitle>
            <CardDescription>{resolvedDescription}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{currentFiles}/{maxFiles} files</Badge>
            <Badge variant="outline">Max size {formatFileSize(maxFileSize)}</Badge>
            {job?.is_locked ? (
              <Badge variant="secondary">
                <Lock className="mr-1 h-3 w-3" />
                Locked
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {allowUpload ? (
          <div className="flex flex-col gap-3 rounded-xl border border-border/70 bg-muted/20 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-medium">Gallery upload</p>
            <p className="text-sm text-muted-foreground">{helperText}</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={uploadKind} onValueChange={(value: MediaUploadKind) => setUploadKind(value)} disabled={!isJobMediaMutable || uploadMutation.isPending}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Media type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Generic</SelectItem>
                {!isEventMediaMode ? <SelectItem value="before">Before</SelectItem> : null}
                {!isEventMediaMode ? <SelectItem value="after">After</SelectItem> : null}
              </SelectContent>
            </Select>
            <Button type="button" onClick={() => openFilePicker()} disabled={!isJobMediaMutable || uploadMutation.isPending || remainingFiles === 0}>
              {uploadMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Add Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              onChange={handleFilesSelected}
            />
          </div>
          </div>
        ) : null}

        {!isPendingMode && mediaQuery.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-72 rounded-xl" />
            ))}
          </div>
        ) : hasAnyMedia ? (
          <div className="space-y-6">
            {mediaSections.map((section) => (
              <section key={section.key} className="space-y-3 rounded-xl border border-border/70 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{section.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{section.items.length + section.pendingItems.length}</Badge>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-2"
                      onClick={() => openFilePicker(section.uploadKind)}
                      disabled={!allowUpload || !isJobMediaMutable || uploadMutation.isPending || remainingFiles === 0}
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Upload {section.title}
                    </Button>
                  </div>
                </div>
                {section.items.length + section.pendingItems.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {section.items.map(renderMediaCard)}
                    {section.pendingItems.map(renderPendingCard)}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border/70 px-4 py-6 text-center text-sm text-muted-foreground">
                    No {section.title.toLowerCase()} media uploaded.
                  </div>
                )}
              </section>
            ))}
          </div>
        ) : (
          <Empty>
            <div className="text-center">
              <p className="text-lg font-medium">No gallery files yet</p>
              <p className="text-sm text-muted-foreground">{`Add images, videos, or supporting files for this ${entityLower}.`}</p>
              {allowUpload ? (
                <Button type="button" className="mt-4" onClick={() => openFilePicker()} disabled={!isJobMediaMutable || uploadMutation.isPending || remainingFiles === 0}>
                  <Upload className="mr-2 h-4 w-4" />
                  Add First File
                </Button>
              ) : null}
            </div>
          </Empty>
        )}
      </CardContent>
    </Card>
  )
}
